import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Divider,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../support/store';
import {
  addTileInstance,
  resetTileState,
  setChartType,
  setQueries,
  setSettings,
  setTileDescription,
  setTileName,
  setTransformations,
  transferTileInstance,
} from '../../../support/slices/tileBuilderSlice';
import {
  useFetchSingleCustomTile,
  useAddCustomTileMutation,
  useUpdateCustomTileMutation,
  useFetchCustomTileCategories,
  useFetchCustomTileViews,
  useAddCustomTileCategoryMutation,
  useAddCustomTileViewMutation,
} from '../../../hooks/toolbox-hooks';
import ChartTypeSelector from './ChartTypeSelector';
import QueryBuilder from './QueryBuilder';
import Transformations from './Transformations';
import TilePreview from './TilePreview/TilePreview';
import { initialTileState, Query } from '../../../types/tileBuilderTypes';
import { CacheKey } from '../../../types/cache-keys';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;

const ADD_SENTINEL = '__add__';

const ENABLE_LOCAL_FILE_TILES =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENABLE_LOCAL_FILES === 'true';

// Helper: normalize queries for app use
function normalizeQuery(q: any): Query {
  return {
    dataSource: q.dataSource ?? '',
    model: q.model ?? '',
    function: q.function ?? '',
    path: q.path ?? '',
    method: q.method ?? 'GET',
    params: q.params ?? {},
    payload: q.payload ?? '',
    queryResult: q.queryResult,
    functionResult: q.functionResult,
    localFilePath: q.localFilePath ?? '',
    localFileName: q.localFileName ?? '',
    ...q,
  };
}

// Helper: remove preview data before saving to backend
function cleanQuery(q: Query) {
  const { queryResult, functionResult, ...rest } = q;
  return rest;
}

type TileBuilderProps = {
  open: boolean;
  editItem: any;
  onClose: () => void;
};

function renderSelectedViews(viewOptions: { id: number; name: string }[], selected: number[]) {
  const idToName = new Map(viewOptions.map((v) => [v.id, v.name]));
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((id) => (
        <Chip key={id} label={idToName.get(id) || String(id)} size="small" />
      ))}
    </Box>
  );
}

/**
 * Compact header: one row for name/type/category/views,
 * and an optional collapse for description.
 */
function TileBuilderCompactHeader({
                                    chartId,
                                    tileName,
                                    tileDescription,
                                    chartType,
                                    dispatch,
                                    categoryOptions,
                                    viewOptions,
                                    selectedCategoryId,
                                    setSelectedCategoryId,
                                    selectedViewIds,
                                    setSelectedViewIds,
                                    isCategoriesLoading,
                                    isViewsLoading,
                                    onOpenAddCategory,
                                    onOpenAddView,
                                  }: {
  chartId: string;
  tileName: string;
  tileDescription: string;
  chartType: string;
  dispatch: any;
  categoryOptions: { id: number; name: string }[];
  viewOptions: { id: number; name: string }[];
  selectedCategoryId: number | '';
  setSelectedCategoryId: (v: any) => void;
  selectedViewIds: number[];
  setSelectedViewIds: (v: any) => void;
  isCategoriesLoading: boolean;
  isViewsLoading: boolean;
  onOpenAddCategory: () => void;
  onOpenAddView: () => void;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    if (value === ADD_SENTINEL) {
      onOpenAddCategory();
      return;
    }
    setSelectedCategoryId(value);
  };

  const handleViewsChange = (event: any) => {
    const value = event.target.value as (number | string)[];
    if (value.includes(ADD_SENTINEL as any)) {
      onOpenAddView();
      setSelectedViewIds(value.filter((v) => v !== ADD_SENTINEL) as number[]);
    } else {
      setSelectedViewIds(value as number[]);
    }
  };

  return (
    <Box sx={{ pb: 1, overflow: 'visible' }}>
      <Box
        sx={{
          overflow: 'visible',
          pt: 0.8,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1.1fr 1fr 1.4fr auto' },
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        <TextField
          label="Tile Name"
          value={tileName}
          onChange={(e) => dispatch(setTileName({ chartId, tileName: e.target.value }))}
          size="small"
          fullWidth
        />

        <Box sx={{ minWidth: 240 }}>
          <ChartTypeSelector
            chartType={chartType}
            setChartType={(t) => dispatch(setChartType({ chartId, chartType: t }))}
            setSettings={(next) => dispatch(setSettings({ chartId, settings: next as any }))}
          />
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel id="category-select-label">Category</InputLabel>
          <Select
            labelId="category-select-label"
            label="Category"
            value={selectedCategoryId === '' ? '' : selectedCategoryId}
            onChange={handleCategoryChange}
            disabled={isCategoriesLoading}
          >
            {categoryOptions.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
            <MenuItem value={ADD_SENTINEL} sx={{ fontStyle: 'italic' }}>
              + Add Category
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id="views-select-label">Views</InputLabel>
          <Select
            labelId="views-select-label"
            label="Views"
            multiple
            value={selectedViewIds}
            onChange={handleViewsChange}
            renderValue={(selected) => renderSelectedViews(viewOptions, selected as number[])}
            disabled={isViewsLoading}
          >
            {viewOptions.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.name}
              </MenuItem>
            ))}
            <MenuItem value={ADD_SENTINEL} sx={{ fontStyle: 'italic' }}>
              + Add View
            </MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
          <Button
            onClick={() => setShowAdvanced((p) => !p)}
            size="small"
            variant="outlined"
            startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            Advanced
          </Button>
        </Box>
      </Box>

      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 1 }}>
          <TextField
            label="Tile Description"
            fullWidth
            multiline
            rows={2}
            value={tileDescription}
            onChange={(e) => dispatch(setTileDescription({ chartId, tileDescription: e.target.value }))}
            size="small"
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function StickyTabs({
                      activeTab,
                      onChange,
                      isQueryAdded,
                      isChartTypeSelected,
                    }: {
  activeTab: number;
  onChange: (_: any, newValue: number) => void;
  isQueryAdded: boolean;
  isChartTypeSelected: boolean;
}) {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs value={activeTab} onChange={onChange}>
        <Tab label="Query Sources" />
        <Tab label="Transformations" disabled={!isQueryAdded} />
        <Tab label="Tile Preview" disabled={!isQueryAdded || !isChartTypeSelected} />
      </Tabs>
    </Box>
  );
}

const TileBuilder: React.FC<TileBuilderProps> = ({
                                                   open,
                                                   editItem,
                                                   onClose,
                                                 }) => {
  const isEditing = !!editItem;
  const editingTileId = editItem?.id || null;
  const PLACEHOLDER_ID = 0;
  const chartId = editingTileId ? `tile-${editingTileId}` : `tile-${PLACEHOLDER_ID}`;

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const tileBuilder = useSelector((state: RootState) => state.tileBuilder[chartId]?.tile || initialTileState);
  const instanceExists = useSelector((state: RootState) => !!state.tileBuilder[chartId]);
  const { tileName, tileDescription, chartType, queries, transformations, settings } = tileBuilder;

  const [activeTab, setActiveTab] = useState(0);

  const { data: editingTile } = useFetchSingleCustomTile(editingTileId, {}, { enabled: !!editingTileId });
  const addTileMutation = useAddCustomTileMutation();
  const updateTileMutation = useUpdateCustomTileMutation();

  const { data: categoriesData, isLoading: isCategoriesLoading } = useFetchCustomTileCategories();
  const { data: viewsData, isLoading: isViewsLoading } = useFetchCustomTileViews();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [selectedViewIds, setSelectedViewIds] = useState<number[]>([]);

  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const addCategoryMutation = useAddCustomTileCategoryMutation();

  const [isAddViewOpen, setIsAddViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const addViewMutation = useAddCustomTileViewMutation();

  const categoryOptions = useMemo(() => {
    const arr = Array.isArray(categoriesData) ? categoriesData : [];
    return arr.map((c: any) => ({ id: c.id, name: c.name }));
  }, [categoriesData]);

  const viewOptions = useMemo(() => {
    const arr = Array.isArray(viewsData) ? viewsData : [];
    return arr.map((v: any) => ({ id: v.id, name: v.name }));
  }, [viewsData]);

  const [initializedTileId, setInitializedTileId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setInitializedTileId(null);
      setActiveTab(0);
      if (!isEditing) dispatch(resetTileState({ chartId }));
      return;
    }

    if (!instanceExists) dispatch(addTileInstance({ chartId }));

    if (isEditing && editingTile && (initializedTileId === null || initializedTileId !== editingTile.id)) {
      dispatch(setTileName({ chartId, tileName: editingTile.name }));
      dispatch(setTileDescription({ chartId, tileDescription: editingTile.description || '' }));
      dispatch(setChartType({ chartId, chartType: editingTile.chart_type }));
      dispatch(setQueries({ chartId, queries: (editingTile.queries || []).map(normalizeQuery) }));
      dispatch(setTransformations({ chartId, transformations: editingTile.transformations || [] }));
      dispatch(setSettings({ chartId, settings: editingTile.settings || {} }));

      const catId = editingTile.category ?? null;
      setSelectedCategoryId(catId == null ? '' : Number(catId));

      const existingViews = Array.isArray(editingTile.views)
        ? editingTile.views.map((link: any) => link?.view?.id).filter((id: any) => typeof id === 'number')
        : [];
      setSelectedViewIds(existingViews);

      setInitializedTileId(editingTile.id);
    }
  }, [open, isEditing, chartId, dispatch, instanceExists, initializedTileId, editingTile]);

  const handleClose = () => {
    setActiveTab(0);
    onClose();
  };

  const handleSaveNewCategory = () => {
    if (!newCategoryName.trim()) return;
    addCategoryMutation.mutate(
      { name: newCategoryName.trim() },
      {
        onSuccess: (created: any) => {
          setIsAddCategoryOpen(false);
          setNewCategoryName('');
          setSelectedCategoryId(created.id);
        },
      }
    );
  };

  const handleCancelNewCategory = () => {
    setIsAddCategoryOpen(false);
    setNewCategoryName('');
  };

  const handleSaveNewView = () => {
    if (!newViewName.trim()) return;
    addViewMutation.mutate(
      { name: newViewName.trim() },
      {
        onSuccess: (created: any) => {
          setIsAddViewOpen(false);
          setNewViewName('');
          setSelectedViewIds((prev) => Array.from(new Set([...prev, created.id])));
        },
      }
    );
  };

  const handleCancelNewView = () => {
    setIsAddViewOpen(false);
    setNewViewName('');
  };

  const buildTileConfig = (nameOverride?: string) => {
    return {
      name: nameOverride ?? tileName,
      description: tileDescription,
      chart_type: chartType,
      queries: queries.map((q) => cleanQuery(q)),
      transformations,
      settings,
      category: selectedCategoryId || null,
      view_links: selectedViewIds.map((vid) => ({ view: vid })),
    };
  };

  const getClonedTileName = (name: string) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'Untitled Tile (Copy)';
    return trimmed.endsWith('(Copy)') ? trimmed : `${trimmed} (Copy)`;
  };

  const handleCloneTile = () => {
    const hasLocalFileQuery = queries.some((q) => q.dataSource === 'Local File');
    if (hasLocalFileQuery && !ENABLE_LOCAL_FILE_TILES) {
      alert('Tiles using Local File are for development only and cannot be saved.');
      return;
    }

    const tileConfig: any = buildTileConfig(getClonedTileName(tileName));

    addTileMutation.mutate(tileConfig, {
      onSuccess: () => {
        alert('Tile cloned successfully!');
        queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'customTiles'] });
      },
      onError: (error: any) => {
        console.error('Error cloning tile:', error);
        alert('Error cloning tile.');
      },
    });
  };

  const handleSaveTile = () => {
    const hasLocalFileQuery = queries.some((q) => q.dataSource === 'Local File');
    if (hasLocalFileQuery && !ENABLE_LOCAL_FILE_TILES) {
      alert('Tiles using Local File are for development only and cannot be saved.');
      return;
    }

    const id = editingTileId;
    const tileConfig: any = buildTileConfig();

    const options = {
      onSuccess: (data: any) => {
        if (!isEditing && data && data.id && chartId === `tile-0`) {
          dispatch(transferTileInstance({ tempId: chartId, realId: `tile-${data.id}` }));
        }
        alert(`Tile ${isEditing ? 'updated' : 'added'} successfully!`);
        queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, 'customTiles'] });
      },
      onError: (error: any) => {
        console.error(`Error ${isEditing ? 'updating' : 'adding'} tile:`, error);
        alert(`Error ${isEditing ? 'updating' : 'adding'} tile.`);
      },
    };

    if (isEditing) updateTileMutation.mutate({ ...tileConfig, id }, options);
    else addTileMutation.mutate(tileConfig, options);
  };

  const isQueryAdded = queries.length > 0;
  const isChartTypeSelected = !!chartType;
  const isMutating = addTileMutation.isPending || updateTileMutation.isPending;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && !isQueryAdded) return;
    if (newValue === 2 && (!isQueryAdded || !isChartTypeSelected)) return;
    setActiveTab(newValue);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '1600px',
            height: '92vh',
          },
        }}
      >
        <DialogTitle sx={{ py: 1 }}>
          {isEditing ? 'Edit Tile' : 'Create Tile'}
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'auto',
            pt: 1,
          }}
        >
          <TileBuilderCompactHeader
            chartId={chartId}
            tileName={tileName}
            tileDescription={tileDescription}
            chartType={chartType}
            dispatch={dispatch}
            categoryOptions={categoryOptions}
            viewOptions={viewOptions}
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
            selectedViewIds={selectedViewIds}
            setSelectedViewIds={setSelectedViewIds}
            isCategoriesLoading={isCategoriesLoading}
            isViewsLoading={isViewsLoading}
            onOpenAddCategory={() => setIsAddCategoryOpen(true)}
            onOpenAddView={() => setIsAddViewOpen(true)}
          />

          <Divider sx={{ my: 1 }} />

          <StickyTabs
            activeTab={activeTab}
            onChange={handleTabChange}
            isQueryAdded={isQueryAdded}
            isChartTypeSelected={isChartTypeSelected}
          />

          <Box sx={{ pt: 2 }}>
            {activeTab === 0 && <QueryBuilder chartId={chartId} />}
            {activeTab === 1 && <Transformations chartId={chartId} />}
            {activeTab === 2 && (
              <TilePreview
                chartId={chartId}
                chartType={chartType}
                chartCategory={selectedCategoryId ? Number(selectedCategoryId) : (undefined as any)}
              />
            )}
          </Box>

          <Box sx={{ height: 8 }} />
        </DialogContent>

        <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 5 }}>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={isMutating}
          >
            Cancel
          </Button>

          {isEditing && (
            <Button
              onClick={handleCloneTile}
              variant="outlined"
              color="primary"
              startIcon={!addTileMutation.isPending ? <ContentCopyIcon /> : undefined}
              disabled={
                isMutating ||
                !isQueryAdded ||
                !isChartTypeSelected
              }
            >
              {addTileMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                'Clone Tile'
              )}
            </Button>
          )}

          <Button
            onClick={handleSaveTile}
            variant="contained"
            color="primary"
            disabled={
              isMutating ||
              activeTab !== 2 ||
              !isQueryAdded ||
              !isChartTypeSelected
            }
          >
            {isMutating ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Update Tile'
            ) : (
              'Save Tile'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isAddCategoryOpen} onClose={handleCancelNewCategory} maxWidth="sm" fullWidth>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewCategory} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCategory}
            variant="contained"
            color="primary"
            disabled={addCategoryMutation.isPending || !newCategoryName.trim()}
          >
            {addCategoryMutation.isPending ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isAddViewOpen} onClose={handleCancelNewView} maxWidth="sm" fullWidth>
        <DialogTitle>Add View</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            fullWidth
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewView} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewView}
            variant="contained"
            color="primary"
            disabled={addViewMutation.isPending || !newViewName.trim()}
          >
            {addViewMutation.isPending ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TileBuilder;